import { type ComponentType } from 'react';

import { defineMessages, type MessageDescriptor } from 'react-intl';

import EpicIcon from '@atlaskit/icon/core/epic';
import GoalIcon from '@atlaskit/icon/core/goal';
import ProjectIcon from '@atlaskit/icon/core/project';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { token } from '@atlaskit/tokens';
import type { IconColor } from '@atlaskit/tokens/css-type-schema';

import type { PinType } from '../types';

/**
 * Accessible labels for each entity type's icon. Single source of truth for the type label copy,
 * shared by every surface that renders a type icon (pinned rows on the For You page and the Your
 * Work search results).
 */
const messages = defineMessages({
	epicLabel: {
		id: 'rovo-pins.typeIconConfig.epicLabel',
		defaultMessage: 'Epic',
		description: 'Accessible label / secondary label for the Jira epic type icon',
	},
	goalLabel: {
		id: 'rovo-pins.typeIconConfig.goalLabel',
		defaultMessage: 'Goal',
		description: 'Accessible label / secondary label for the Atlas goal type icon',
	},
	projectLabel: {
		id: 'rovo-pins.typeIconConfig.projectLabel',
		defaultMessage: 'Project',
		description: 'Accessible label / secondary label for the Atlas project type icon',
	},
});

/** The icon, accent color, and label for a single entity type. */
export interface TypeIconConfig {
	icon: ComponentType<NewCoreIconProps>;
	color: IconColor;
	label: MessageDescriptor;
}

/**
 * Single source of truth for how each entity type is represented visually: icon glyph, accent
 * color, and label. Keyed by {@link PinType} (the backend-canonical vocabulary) so both the pinned
 * items UI (which already speaks `PinType`) and the search results (which map their lowercase
 * `category` via `categoryToPinType`) render identical iconography.
 *
 * - Epic → purple {@link EpicIcon}
 * - Project → blue {@link ProjectIcon} (the rocket)
 * - Goal → gray {@link GoalIcon}
 *
 * Exhaustive `Record`, so adding a new `PinType` is a compile error until it's given a config.
 */
export const TYPE_ICON_CONFIG: Record<PinType, TypeIconConfig> = {
	JiraIssue: {
		icon: EpicIcon,
		color: token('color.icon.accent.purple'),
		label: messages.epicLabel,
	},
	AtlasProject: {
		icon: ProjectIcon,
		color: token('color.icon.accent.blue'),
		label: messages.projectLabel,
	},
	AtlasGoal: {
		icon: GoalIcon,
		color: token('color.icon.accent.gray'),
		label: messages.goalLabel,
	},
};
